import React, { useState, useEffect } from 'react';
import { BasePageProps, NotificationTemplate, NotificationEventTypes, CHANNELS, RECIPIENT_TYPES } from "app/types";
import { AppTopNav } from "../../shared/AppTopNav";
import { NotificationTemplateEditor } from './NotificationTemplateEditor';
import { Modal, Button } from 'react-bootstrap-v5';
import { KTSVG } from '_metronic/helpers';
import { NotificationTemplateService } from 'app/services/NotificationTemplateService';
import { useHistory } from 'react-router-dom';
import JSZip from 'jszip';
import saveAs from 'file-saver';

export const AdminNotificationTemplates: React.FC<BasePageProps> = (props: BasePageProps) => {
    const notificationTemplateService = new NotificationTemplateService();
    const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<NotificationTemplate[]>([]);
    const [filters, setFilters] = useState({
        eventType: '',
        recipientType: '',
        channel: '',
        searchTerm: '',
    });
    const [showEditor, setShowEditor] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showTestModal, setShowTestModal] = useState(false);
    const [testRecipient, setTestRecipient] = useState('');
    const [testData, setTestData] = useState('{}');
    const [testLoading, setTestLoading] = useState(false);
    const [testError, setTestError] = useState<string | null>(null);
    const [testingTemplate, setTestingTemplate] = useState<NotificationTemplate | null>(null);
    const history = useHistory();
    // Fetch templates from API
    useEffect(() => {
        setLoading(true);
        notificationTemplateService.listTemplates()
            .then((data) => {
                // Ensure data is always an array
                setTemplates(Array.isArray(data) ? data : (data ? [data] : []));
            })
            .catch((err) => setError(err.message || 'Failed to fetch templates'))
            .finally(() => setLoading(false));
    }, []);

    // Apply filters
    useEffect(() => {
        let result = [...templates];

        if (filters.eventType) {
            result = result.filter(t => t.eventType === filters.eventType);
        }
        if (filters.recipientType) {
            result = result.filter(t => t.recipientType === filters.recipientType);
        }
        if (filters.channel) {
            result = result.filter(t => t.channel === filters.channel);
        }
        if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            result = result.filter(t =>
                t.templateId.toLowerCase().includes(searchLower) ||
                t.body.toLowerCase().includes(searchLower)
            );
        }

        setFilteredTemplates(result);
    }, [templates, filters]);

    const handleSave = async (template: NotificationTemplate) => {
        setLoading(true);
        setError(null);
        try {
            let saved: NotificationTemplate;
            if (templates.some(t => t.templateId === template.templateId && t.channel === template.channel)) {
                saved = await notificationTemplateService.updateTemplate(template);
                setTemplates(prev => prev.map(t => (t.templateId === saved.templateId && t.channel === saved.channel) ? saved : t));
            } else {
                saved = await notificationTemplateService.addTemplate(template);
                setTemplates(prev => [...prev, saved]);
            }
            setShowEditor(false);
        } catch (err: any) {
            setError(err.message || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    const handleTestSubmit = async () => {
        if (!testingTemplate) return;

        setTestLoading(true);
        setTestError(null);
        try {
            let parsedTestData = {};
            try {
                parsedTestData = JSON.parse(testData);
            } catch (e) {
                throw new Error('Invalid JSON format for test data');
            }

            await notificationTemplateService.testTemplate(
                testingTemplate.templateId,
                testingTemplate.channel,
                testRecipient,
                parsedTestData
            );
            setShowTestModal(false);
        } catch (error: any) {
            setTestError(error.message);
        } finally {
            setTestLoading(false);
        }
    };

    const handleDump = async () => {
        setLoading(true);
        try {
            const zip = new JSZip();
            const folder = zip.folder("notification_templates");
            
            templates.forEach(template => {
                const fileName = `${template.templateId}_${template.channel}.json`;
                folder?.file(fileName, JSON.stringify(template, null, 2));
            });

            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, `notification_templates_dump_${new Date().toISOString().split('T')[0]}.zip`);
        } catch (err: any) {
            setError('Failed to create dump: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setLoading(true);
        setError(null);
        try {
            const zip = await JSZip.loadAsync(file);
            const importedTemplates: NotificationTemplate[] = [];

            // Find all JSON files in the zip
            const files = Object.keys(zip.files).filter(name => name.endsWith('.json'));

            for (const fileName of files) {
                const content = await zip.files[fileName].async('string');
                try {
                    const template = JSON.parse(content) as NotificationTemplate;
                    // Basic validation
                    if (template.templateId && template.channel && template.body) {
                        importedTemplates.push(template);
                    }
                } catch (e) {
                    console.error(`Failed to parse ${fileName}`, e);
                }
            }

            if (importedTemplates.length === 0) {
                throw new Error('No valid templates found in the zip file');
            }

            if (!window.confirm(`Found ${importedTemplates.length} templates. Do you want to restore them? This will overwrite existing templates with the same ID and Channel.`)) {
                return;
            }

            // Upsert templates
            for (const template of importedTemplates) {
                const exists = templates.some(t => t.templateId === template.templateId && t.channel === template.channel);
                if (exists) {
                    await notificationTemplateService.updateTemplate(template);
                } else {
                    await notificationTemplateService.addTemplate(template);
                }
            }

            // Refresh templates list
            const freshTemplates = await notificationTemplateService.listTemplates();
            setTemplates(freshTemplates);
            alert('Restore completed successfully!');
        } catch (err: any) {
            setError(err.message || 'Failed to restore templates');
        } finally {
            setLoading(false);
            // Clear input
            event.target.value = '';
        }
    };

  return (
    <div className='chatPage'>
      <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />
      <div className='container-fluid mt-10'>
        {/* Filters Section */}
        <div className='card mb-5 mb-xl-8'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Notification Templates</span>
            </h3>
            <div className='card-toolbar'>
              <button
                className='btn btn-sm btn-light-info me-2'
                onClick={handleDump}
                disabled={loading || templates.length === 0}
              >
                <KTSVG path='/media/icons/duotone/Files/Download.svg' className='svg-icon-3' />
                Dump .zip
              </button>
              <label className={`btn btn-sm btn-light-warning me-2 mb-0 ${loading ? 'disabled' : ''}`}>
                <KTSVG path='/media/icons/duotone/Files/Upload.svg' className='svg-icon-3' />
                Restore .zip
                <input
                  type='file'
                  accept='.zip'
                  style={{display: 'none'}}
                  onChange={handleRestore}
                  disabled={loading}
                />
              </label>
              <button
                className='btn btn-sm btn-light-primary'
                onClick={() => {
                  setSelectedTemplate(null)
                  setShowEditor(true)
                }}
              >
                <KTSVG path='/media/icons/duotone/Navigation/plus.svg' className='svg-icon-3' />
                New Template
              </button>
            </div>
          </div>
          <div className='card-body py-3'>
            {/* Filters */}
            <div className='d-flex flex-wrap gap-5 mb-5'>
              <div className='w-200px'>
                <select
                  className='form-select'
                  value={filters.eventType}
                  onChange={(e) => setFilters((prev) => ({...prev, eventType: e.target.value}))}
                >
                  <option value=''>All Event Types</option>
                  {Object.keys(NotificationEventTypes).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className='w-200px'>
                <select
                  className='form-select'
                  value={filters.recipientType}
                  onChange={(e) => setFilters((prev) => ({...prev, recipientType: e.target.value}))}
                >
                  <option value=''>All Recipient Types</option>
                  {RECIPIENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className='w-200px'>
                <select
                  className='form-select'
                  value={filters.channel}
                  onChange={(e) => setFilters((prev) => ({...prev, channel: e.target.value}))}
                >
                  <option value=''>All Channels</option>
                  {CHANNELS.map((channel) => (
                    <option key={channel} value={channel}>
                      {channel.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className='w-300px'>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Search templates...'
                  value={filters.searchTerm}
                  onChange={(e) => setFilters((prev) => ({...prev, searchTerm: e.target.value}))}
                />
              </div>
            </div>

                        {loading && <div className='fw-bold'>Loading ...</div>}
                        {/* Templates Table */}
                        <div className='table-responsive'>

                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        <th className='min-w-150px'>Template ID</th>
                                        <th className='min-w-100px'>Event Type</th>
                                        <th className='min-w-100px'>Channel</th>
                                        <th className='min-w-100px'>Recipient</th>
                                        <th className='min-w-100px'>Status</th>
                                        <th className='min-w-100px text-end'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTemplates.map((template) => (
                                        <tr key={template.templateId}>
                                            <td>{template.templateId}</td>
                                            <td>{template.eventType}</td>
                                            <td>{template.channel.toUpperCase()}</td>
                                            <td>{template.recipientType}</td>
                                            <td>
                                                <span className={`badge badge-light-${template.enabled ? 'success' : 'danger'}`}>
                                                    {template.enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </td>
                                            <td className='text-end'>
                                                <button
                                                    className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                                    onClick={() => {
                                                        setSelectedTemplate(template);
                                                        setShowEditor(true);
                                                    }}
                                                >
                                                    <KTSVG path='/media/icons/duotone/General/Edit.svg' className='svg-icon-3' />
                                                </button>
                                                {/* <button
                                                    className='btn btn-icon btn-bg-light btn-active-color-success btn-sm'
                                                    onClick={() => {
                                                        setTestingTemplate(template);
                                                        setTestRecipient('');
                                                        setTestData('{}');
                                                        setShowTestModal(true);
                                                    }}
                                                >
                                                    <KTSVG path='/media/icons/duotone/Communication/Send.svg' className='svg-icon-3' />
                                                </button> */}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Template Editor Modal */}
                <Modal
                    show={showEditor}
                    onHide={() => setShowEditor(false)}
                    size="lg"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedTemplate ? 'Edit Template' : 'New Template'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <NotificationTemplateEditor
                            template={selectedTemplate}
                            onSave={handleSave}
                        />
                    </Modal.Body>
                </Modal>

                {/* Test Template Modal */}
                <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Test Template: {testingTemplate?.templateId}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {testError && (
                            <div className="alert alert-danger">{testError}</div>
                        )}
                        <div className="mb-3">
                            <label className="form-label">
                                {testingTemplate?.channel === 'email' ? 'Email Address' : 'Phone Number'}
                            </label>
                            <input
                                type={testingTemplate?.channel === 'email' ? 'email' : 'tel'}
                                className="form-control"
                                value={testRecipient}
                                onChange={(e) => setTestRecipient(e.target.value)}
                                placeholder={testingTemplate?.channel === 'email' ?
                                    'Enter email address' :
                                    'Enter phone number (E.164 format: +1234567890)'}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Test Data (JSON)</label>
                            <textarea
                                className="form-control font-monospace"
                                rows={5}
                                value={testData}
                                onChange={(e) => setTestData(e.target.value)}
                                placeholder="Enter test data in JSON format"
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowTestModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleTestSubmit}
                            disabled={testLoading || !testRecipient}
                        >
                            {testLoading ? 'Sending...' : 'Send Test'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
}